/**
 * Represents an indexed list of objects. Each should have an `id` property.
 * Objects can be retrieved by index or by id.
 *
 * TODO: `splice()` should return original if unchanged.
 *
 */
import isObject from './isObject';

type MapIterator<T> = (item: T, i: number) => T;
type FilterIterator<T> = (item: T, i: number) => boolean;
type SortFunction = (a: unknown, b: unknown) => number;
type Reducer<T, U> = (previousResult: U, item: T, i: number) => U;
type Mapper<T, U> = (item: T, i: number) => U;

const ERROR_INVALID_ARG =
  'First argument to IndexedList() must be an array of objects.';

const localeCompareProto = String.prototype.localeCompare;

const localeCompare: SortFunction = (a, b) =>
  localeCompareProto.call(
    a == null ? '' : String(a),
    b == null ? '' : String(b),
  );

class IndexedList<T extends { id: string } | null> {
  length: number;
  list: Array<T>;
  index: Map<string, number>;

  constructor(items: Array<T>) {
    if (!Array.isArray(items)) {
      throw new Error(ERROR_INVALID_ARG);
    }
    let index = (this.index = new Map<string, number>());
    this.list = items.map((item, i) => {
      if (item != null && item.id != null) {
        let id = typeof item.id === 'string' ? item.id : '';
        index.set(id, i);
      }
      return item;
    });
    this.length = this.list.length;
  }

  getIndexByID(id: string): number {
    let index = this.index.get(id);
    return index == null ? -1 : index;
  }

  hasID(id: string): boolean {
    return this.index.has(id);
  }

  getByID(id: string): T | null | undefined {
    let index = this.index.get(id);
    return index == null ? undefined : this.list[index];
  }

  setByID(id: string, item: T): IndexedList<T> {
    let index = this.getIndexByID(id);
    return index === -1 ? this : this.setByIndex(index, item);
  }

  removeByID(id: string): IndexedList<T> {
    let index = this.getIndexByID(id);
    if (index === -1) {
      return this;
    }
    let newList = this.list.slice();
    newList.splice(index, 1);
    return new IndexedList(newList);
  }

  getByIndex(i: number): T | null | undefined {
    return this.list[i];
  }

  indexOf(item: T | null | undefined): number {
    if (item != null && typeof item.id === 'string') {
      return this.getIndexByID(item.id);
    } else {
      return -1;
    }
  }

  includes(item: T | null | undefined): boolean {
    if (item != null && typeof item.id === 'string') {
      return this.hasID(item.id);
    } else {
      return false;
    }
  }

  setByIndex(i: number, item: T): IndexedList<T> {
    if (this.list[i] === item) {
      return this;
    }
    let newList = this.list.slice();
    newList[i] = item;
    return new IndexedList(newList);
  }

  sort(sortFunc: SortFunction): IndexedList<T> {
    if (!sortFunc) {
      throw new Error('IndexedList.sort(): sort function not provided.');
    }
    let newList = this.list.slice();
    newList.sort(sortFunc);
    let didChange = this.list.some((item, i) => item !== newList[i]);
    if (!didChange) {
      return this;
    }
    return new IndexedList(newList);
  }

  sortByKey(
    key: string,
    sortFunc: SortFunction = localeCompare,
  ): IndexedList<T> {
    let newList = this.list.slice();
    newList.sort((a: T | null | undefined, b: T | null | undefined) =>
      sortFunc(get(a, key), get(b, key)),
    );
    let didChange = this.list.some((item, i) => item !== newList[i]);
    return didChange ? new IndexedList(newList) : this;
  }

  first(): T | null | undefined {
    return this.list[0];
  }

  last(): T | null | undefined {
    return this.list[this.list.length - 1];
  }

  getList(): Array<T> {
    return this.list;
  }

  // The honeypot is there to ensure we don't accidentally pass a function
  // like in the previous API.
  toArray(_honeypot?: number): Array<T> {
    return this.list.slice();
  }

  mapToArray<U>(fn: Mapper<T, U>): Array<U> {
    return this.list.map(fn);
  }

  toJSON(): Array<T> {
    return this.list.slice();
  }

  // For these methods (every, find, findIndex, forEach, reduce, some) we can
  // simply defer to the Array behavior.
  every(fn: FilterIterator<T>, context?: unknown): boolean {
    let { list } = this;
    return context === undefined ? list.every(fn) : list.every(fn, context);
  }
  find(fn: FilterIterator<T>, context?: unknown): T | null | undefined {
    let { list } = this;
    return context === undefined ? list.find(fn) : list.find(fn, context);
  }
  findIndex(fn: FilterIterator<T>, context?: unknown): number {
    let { list } = this;
    return context === undefined
      ? list.findIndex(fn)
      : list.findIndex(fn, context);
  }
  forEach(fn: (item: T, i: number) => void, context?: unknown): void {
    let { list } = this;
    context === undefined ? list.forEach(fn) : list.forEach(fn, context);
  }
  reduce<U>(fn: Reducer<T, U>, init: U): U {
    return this.list.reduce(fn, init);
  }
  some(fn: FilterIterator<T>, context?: unknown): boolean {
    let { list } = this;
    return context === undefined ? list.some(fn) : list.some(fn, context);
  }

  // These are pure methods for which we can use a modified version of the
  // Array behavior.
  filter(fn: FilterIterator<T>, context?: unknown): IndexedList<T> {
    let { list } = this;
    let newList =
      context === undefined ? list.filter(fn) : list.filter(fn, context);
    return new IndexedList(newList);
  }
  map(fn: MapIterator<T>, context?: unknown): IndexedList<T> {
    let { list } = this;
    let newList = context === undefined ? list.map(fn) : list.map(fn, context);
    return new IndexedList(newList);
  }
  slice(start: number, end?: number): IndexedList<T> {
    let { list } = this;
    let newList =
      end === undefined ? list.slice(start) : list.slice(start, end);
    return new IndexedList(newList);
  }
  concat(list: Array<T>): IndexedList<T> {
    let newList = this.list.concat(list);
    return new IndexedList(newList);
  }

  // These are mutative methods for which we can use a modified version of the
  // Array behavior.
  push(...items: Array<T>): IndexedList<T> {
    let newList = this.list.slice();
    newList.push(...items);
    return new IndexedList(newList);
  }
  splice(
    start: number,
    deleteCount: number,
    ...items: Array<T>
  ): IndexedList<T> {
    let newList = this.list.slice();
    newList.splice(start, deleteCount, ...items);
    return new IndexedList(newList);
  }
  unshift(...items: Array<T>): IndexedList<T> {
    let newList = this.list.slice();
    newList.unshift(...items);
    return new IndexedList(newList);
  }
}

function get(obj: unknown, key: string): unknown {
  return isObject(obj) ? obj[key] : undefined;
}

export default IndexedList;
